import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export interface PedidoListItem {
  id: string;
  numero_orden?: number | null;
  project_id: string;
  ot_id: string;
  tipo: string;
  estado: string;
  notas: string | null;
  items_count: number;
  created_by: { id: string; nombre: string };
  created_at: string;
  updated_at: string;
}

export interface PedidoItemDetail {
  id: string;
  material_id: string | null;
  material_nombre: string | null;
  material_sku: string | null;
  material_unidad: string | null;
  material_categoria: string | null;
  equipo_id: string | null;
  equipo_nombre: string | null;
  insumo_id: string | null;
  insumo_detalle: string | null;
  insumo_presentacion: string | null;
  insumo_precio_unitario: number | null;
  nombre_custom: string | null;
  unidad_custom: string | null;
  cantidad: number;
  dispatched_qty: number;
  es_consumible_adicional: boolean;
  es_material_adicional: boolean;
  justificacion: string | null;
  excede_ot: boolean;
}

export interface PedidoDetail extends PedidoListItem {
  items: PedidoItemDetail[];
}

interface PedidoListResponse {
  data: PedidoListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function usePedidos(projectId: string, filters?: { estado?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.pedidos.list({ projectId, ...filters }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.estado) params.set('estado', filters.estado);
      if (filters?.page) params.set('page', String(filters.page));
      if (filters?.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const res = await api.get<PedidoListResponse>(`/projects/${projectId}/pedidos${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

// ── All pedidos (cross-project, for Compras) ──
export interface PedidoGlobalItem {
  id: string;
  numero: number;
  numero_orden?: number | null;
  project_id: string;
  project_nombre: string;
  project_codigo: string;
  tipo: string;
  estado: string;
  notas: string | null;
  created_by: { nombre: string };
  created_at: string;
}

export function useAllPedidos(filters?: { estado?: string }) {
  return useQuery({
    queryKey: ['pedidos', 'all', filters?.estado],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.estado) params.set('estado', filters.estado);
      params.set('limit', '50');
      const qs = params.toString();
      const res = await api.get<{ data: PedidoGlobalItem[]; pagination: { total: number } }>(`/pedidos${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });
}

export function usePedidoDetail(pedidoId: string) {
  return useQuery({
    queryKey: queryKeys.pedidos.detail(pedidoId),
    queryFn: async () => {
      const res = await api.get<{ data: PedidoDetail }>(`/pedidos/${pedidoId}`);
      return res.data.data;
    },
    enabled: !!pedidoId,
  });
}

export function useCreatePedido(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      tipo: string;
      notas?: string | null;
      items: Array<{
        materialId?: string | null;
        equipoId?: string | null;
        nombreCustom?: string | null;
        unidadCustom?: string | null;
        cantidad: number;
        esConsumibleAdicional?: boolean;
        justificacion?: string | null;
      }>;
    }) => {
      const res = await api.post<{ data: PedidoDetail }>(`/projects/${projectId}/pedidos`, data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.materialBalance.byProject(projectId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projectSummary.detail(projectId) });
    },
  });
}

export function useCancelPedido() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { pedidoId: string }) => {
      const res = await api.put<{ data: { id: string; estado: string } }>(`/pedidos/${vars.pedidoId}/cancel`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.all });
    },
  });
}

export function useUpdatePedidoItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      pedidoId: string;
      items: Array<{ materialId?: string | null; equipoId?: string | null; cantidad: number }>;
    }) => {
      const res = await api.put<{ data: PedidoDetail }>(`/pedidos/${vars.pedidoId}/items`, { items: vars.items });
      return res.data.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.pedidos.detail(vars.pedidoId) });
      queryClient.invalidateQueries({ queryKey: ['pedidos', 'all'] });
    },
  });
}

export function useOrdenMaterialesPdf() {
  return useMutation({
    mutationFn: async ({ pedidoId, maestroId }: { pedidoId: string; maestroId?: string | null }) => {
      const params = maestroId ? `?maestroId=${encodeURIComponent(maestroId)}` : '';
      const res = await api.get<Blob>(`/pedidos/${pedidoId}/orden-pdf${params}`, {
        responseType: 'blob',
      });
      return { data: res.data, pedidoId };
    },
    onSuccess: ({ data: blob, pedidoId }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orden-materiales-${pedidoId.slice(-6)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useAddItemAdicional() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pedidoId,
      data,
    }: {
      pedidoId: string;
      data: {
        tipo: 'insumo' | 'material_adicional';
        insumoId?: string;
        materialId?: string;
        cantidad: number;
        justificacion?: string;
      };
    }) => {
      const res = await api.post<{ data: PedidoDetail }>(`/pedidos/${pedidoId}/items/adicionales`, data);
      return res.data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.pedidos.detail(vars.pedidoId) });
    },
  });
}

export function useDeletePedidoItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, pedidoId }: { itemId: string; pedidoId: string }) => {
      await api.delete(`/pedido-items/${itemId}`);
      return pedidoId;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.pedidos.detail(vars.pedidoId) });
    },
  });
}
